import React, { useCallback, useEffect, useRef, useState } from 'react'

const TRANSFORMACIONES = [
    { nombre: 'Liliana', edad: '+50', tags: 'Salud · Fuerza · Longevidad', img: '/assets/transformaciones/liliana.jpeg' },
    { nombre: 'Gisela', edad: '+40', tags: 'Recomposición corporal · Salud digestiva', img: '/assets/transformaciones/gisela.jpeg' },
    { nombre: 'Silvia', edad: '+60', tags: 'Fuerza · Hábitos · Calidad de vida', img: '/assets/transformaciones/silvia.jpeg' },
    { nombre: 'Sergio', edad: '+45', tags: 'Rendimiento · Físico · 3 días de entrenamiento', img: '/assets/transformaciones/sergio.jpeg' },
    { nombre: 'Teresa', edad: '+50', tags: '−20 kg · Movilidad · Calidad de vida', img: '/assets/transformaciones/teresa.jpeg' },
    { nombre: 'Jesús', edad: '+50', tags: 'Salud · Longevidad · Mejora física', img: '/assets/transformaciones/jesus-50.jpeg' },
]

const MAS_TRANSFORMACIONES = [
    { nombre: 'Lázaro', edad: '+30', tags: 'De querer mejorar a atreverse a competir', img: '/assets/transformaciones/lazaro.jpeg' },
    { nombre: 'Alberto', edad: '+40', tags: 'Varias operaciones de menisco, en su mejor estado', img: '/assets/transformaciones/alberto.jpeg' },
    { nombre: 'Jesús', edad: '+40', tags: 'De la desmotivación a volver a creer en su progreso', img: '/assets/transformaciones/jesus-40.jpeg' },
]

const ALL_TRANSFORMACIONES = [...TRANSFORMACIONES, ...MAS_TRANSFORMACIONES]

const AUTOPLAY_INTERVAL_MS = 3500
const RESUME_AFTER_MS = 5000

const TransformacionCard = ({ persona, onImageClick }) => (
    <div data-card className='w-[260px] md:w-[300px] shrink-0 snap-start'>
        <img
            className='w-full rounded-2xl object-cover cursor-zoom-in'
            src={persona.img}
            alt={`Transformación de ${persona.nombre}`}
            onClick={() => onImageClick(persona)}
        />
        <p className='mt-3 font-black'>{persona.nombre} <span className='text-sbPallet-accent'>{persona.edad}</span></p>
        <p className='text-sm text-white/60'>{persona.tags}</p>
    </div>
)

const Results = () => {
    const trackRef = useRef(null)
    const resumeTimeoutRef = useRef(null)
    const [isPaused, setIsPaused] = useState(false)
    const [selected, setSelected] = useState(null)

    const getStep = useCallback(() => {
        const track = trackRef.current
        if (!track) return 0
        const card = track.querySelector('[data-card]')
        return card ? card.getBoundingClientRect().width + 24 : track.clientWidth * 0.8
    }, [])

    const scrollByStep = useCallback((direction) => {
        const track = trackRef.current
        if (!track) return
        track.scrollBy({ left: getStep() * direction, behavior: 'smooth' })
    }, [getStep])

    const pauseAutoplay = useCallback(() => {
        setIsPaused(true)
        if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current)
        resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), RESUME_AFTER_MS)
    }, [])

    useEffect(() => {
        if (isPaused || selected) return undefined

        const interval = setInterval(() => {
            const track = trackRef.current
            if (!track) return
            const maxScroll = track.scrollWidth - track.clientWidth
            if (track.scrollLeft >= maxScroll - 5) {
                track.scrollTo({ left: 0, behavior: 'smooth' })
            } else {
                scrollByStep(1)
            }
        }, AUTOPLAY_INTERVAL_MS)

        return () => clearInterval(interval)
    }, [isPaused, selected, scrollByStep])

    useEffect(() => () => {
        if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current)
    }, [])

    useEffect(() => {
        if (!selected) return undefined
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setSelected(null)
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [selected])

    return (
        <section className='w-full bg-bgBlack py-16 px-6 flex flex-col items-center' id="results">
            <p className='text-sbPallet-accent font-bold uppercase tracking-widest text-sm'>Resultados reales</p>
            <h2 className='sr-only'>+1000 cambios y +20 años de experiencia</h2>
            <div className='mt-4 flex items-stretch justify-center gap-6 sm:gap-12 lg:gap-16' data-testid="results-stats">
                <div className='flex flex-col items-center text-center'>
                    <span className='text-5xl sm:text-6xl lg:text-7xl font-black leading-none'><span className='text-sbPallet-accent'>+</span>1000</span>
                    <span className='mt-2 text-sm lg:text-base font-bold uppercase tracking-widest text-white/80'>cambios</span>
                </div>
                <div className='w-px bg-white/15' aria-hidden="true"/>
                <div className='flex flex-col items-center text-center'>
                    <span className='text-5xl sm:text-6xl lg:text-7xl font-black leading-none'><span className='text-sbPallet-accent'>+</span>20</span>
                    <span className='mt-2 text-sm lg:text-base font-bold uppercase tracking-widest text-white/80'>años de experiencia</span>
                </div>
            </div>
            <p className='mt-3 text-white/70 text-center max-w-lg'>Personas reales. Objetivos diferentes. Estrategias individualizadas.</p>

            <div
                className='relative mt-10 w-full max-w-6xl'
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
            >
                <div
                    ref={trackRef}
                    onTouchStart={pauseAutoplay}
                    onPointerDown={pauseAutoplay}
                    className='no-scrollbar flex gap-6 overflow-x-auto snap-x snap-mandatory pb-4 scroll-smooth'
                >
                    {ALL_TRANSFORMACIONES.map((persona) => (
                        <TransformacionCard key={persona.nombre + persona.tags} persona={persona} onImageClick={setSelected}/>
                    ))}
                </div>

                <button
                    onClick={() => { pauseAutoplay(); scrollByStep(-1) }}
                    aria-label="Transformación anterior"
                    className='hidden md:flex absolute left-0 top-[calc(50%-1rem)] -translate-y-1/2 -translate-x-4 items-center justify-center w-10 h-10 rounded-full bg-black/60 border border-white/20 hover:border-sbPallet-accent hover:text-sbPallet-accent transition-colors'
                >
                    ‹
                </button>
                <button
                    onClick={() => { pauseAutoplay(); scrollByStep(1) }}
                    aria-label="Siguiente transformación"
                    className='hidden md:flex absolute right-0 top-[calc(50%-1rem)] -translate-y-1/2 translate-x-4 items-center justify-center w-10 h-10 rounded-full bg-black/60 border border-white/20 hover:border-sbPallet-accent hover:text-sbPallet-accent transition-colors'
                >
                    ›
                </button>
            </div>

            {selected && (
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-6'
                    onClick={() => setSelected(null)}
                >
                    <button
                        onClick={() => setSelected(null)}
                        aria-label="Cerrar"
                        className='absolute top-4 right-4 md:top-6 md:right-6 flex items-center justify-center w-10 h-10 rounded-full bg-black/60 border border-white/20 hover:border-sbPallet-accent hover:text-sbPallet-accent transition-colors'
                    >
                        ✕
                    </button>
                    <div className='max-w-3xl w-full flex flex-col items-center' onClick={(e) => e.stopPropagation()}>
                        <img
                            className='max-h-[80vh] w-auto rounded-2xl object-contain'
                            src={selected.img}
                            alt={`Transformación de ${selected.nombre}`}
                        />
                        <p className='mt-4 font-black text-lg'>{selected.nombre} <span className='text-sbPallet-accent'>{selected.edad}</span></p>
                        <p className='text-sm text-white/60'>{selected.tags}</p>
                    </div>
                </div>
            )}
        </section>
    )
}

export default Results
